import React, { useState } from "react";
import { Modal } from "../../../evo/bootstrap";
import {
  formatDisplayDate,
  formatDisplayTime,
  calculateNights,
  maskEmail,
  maskPhone,
} from "../../utils/bookingHistoryUtils";

const BookingDetailsModal = ({ show, onHide, group, attemptNumber }: any) => {
  const [openAccordions, setOpenAccordions] = useState<any>({});

  if (!group) return null;

  const toggleAccordion = (key) => {
    setOpenAccordions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Group actions by date selection
  const groupActionsByDate = () => {
    const dateGroups = [];
    let currentDateGroup = null;

    group.records.forEach((record) => {
      const { action, data } = record.bookingData;

      // If this is a dates_selected action, start a new date group
      if (action === "dates_selected" && data.checkin_date && data.checkout_date) {
        // Save previous group if exists
        if (currentDateGroup) {
          dateGroups.push(currentDateGroup);
        }
        // Start new group
        currentDateGroup = {
          checkinDate: data.checkin_date,
          checkoutDate: data.checkout_date,
          dateRecord: record,
          actions: [],
        };
      } else {
        // Add to current group, or create a fallback group if none exists
        if (currentDateGroup) {
          currentDateGroup.actions.push(record);
        } else {
          // Create fallback group with dates from this action if available
          const checkin = data.checkin_date || group.checkinDate;
          const checkout = data.checkout_date || group.checkoutDate;
          currentDateGroup = {
            checkinDate: checkin,
            checkoutDate: checkout,
            dateRecord: null,
            actions: [record],
          };
        }
      }
    });

    // Add the last group
    if (currentDateGroup) {
      dateGroups.push(currentDateGroup);
    }

    return dateGroups;
  };

  const dateGroups = groupActionsByDate();

  // Get most important information first
  const getKeyInfo = () => {
    const bookingConfirmed = group.records.find((r) => r.bookingData?.action === "booking_confirmed");
    const datesSelected = group.records.find((r) => r.bookingData?.action === "dates_selected");
    const roomSelected = group.records.find((r) => r.bookingData?.action === "room_selected");
    const offerSelected = group.records.find((r) => r.bookingData?.action === "offer_selected");
    const roomsPresented = group.records.find((r) => r.bookingData?.action === "rooms_presented");
    const occupancySelected = group.records.find((r) => r.bookingData?.action === "occupancy_selected");

    const guestDetails = (() => {
      const userFromConfirmation = bookingConfirmed?.bookingData?.data?.user_details || {};
      const userFromDates = datesSelected?.bookingData?.data || {};

      const firstName = userFromConfirmation.first_name || userFromDates.user_first_name;
      const lastName = userFromConfirmation.last_name || userFromDates.user_last_name;
      const email = userFromConfirmation.email || userFromDates.user_email;
      const phone = userFromConfirmation.phone || userFromDates.user_phone;

      if (firstName || lastName || email || phone) {
        return { firstName, lastName, email, phone };
      }
      return null;
    })();

    return {
      bookingConfirmed,
      datesSelected,
      roomSelected,
      roomsPresented,
      occupancySelected,
      guestDetails,
      finalRate: occupancySelected?.bookingData?.data?.final_rate,
      currency:
        occupancySelected?.bookingData?.data?.currency ||
        offerSelected?.bookingData?.data?.currency ||
        roomSelected?.bookingData?.data?.currency ||
        roomsPresented?.bookingData?.data?.currency,
      checkinDate: bookingConfirmed?.bookingData?.data?.checkin_date || 
                   datesSelected?.bookingData?.data?.checkin_date ||
                   roomsPresented?.bookingData?.data?.checkin_date ||
                   group.checkinDate,
      checkoutDate: bookingConfirmed?.bookingData?.data?.checkout_date || 
                    datesSelected?.bookingData?.data?.checkout_date ||
                    roomsPresented?.bookingData?.data?.checkout_date ||
                    group.checkoutDate,
    };
  };

  const keyInfo = getKeyInfo();

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="py-2 px-3">
        <Modal.Title className="small fw-semibold" style={{ fontSize: "14px" }}>
          Booking Attempt #{attemptNumber}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-2" style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {/* Key Information - Always Visible */}
        <div className="booking-key-info-compact mb-3">
          {/* Guest Summary Row */}
          {keyInfo.guestDetails && (
            <div className="booking-summary-row-icons-compact mb-2">
              <div className="booking-summary-item-icon-compact">
                <i className="bi bi-person text-primary"></i>
                <span className="booking-label-compact">Guest: </span>
                <span className="booking-value-compact fw-bold">
                  {keyInfo.guestDetails.firstName} {keyInfo.guestDetails.lastName || ""}
                </span>
              </div>
              <div className="booking-divider-vertical-compact"></div>
              <div className="booking-summary-item-icon-compact">
                <i className="bi bi-envelope text-primary"></i>
                <span className="booking-label-compact">Email: </span>
                <span className="booking-value-compact fw-bold">{maskEmail(keyInfo.guestDetails.email)}</span>
              </div>
              <div className="booking-divider-vertical-compact"></div>
              <div className="booking-summary-item-icon-compact">
                <i className="bi bi-telephone text-primary"></i>
                <span className="booking-label-compact">Phone: </span>
                <span className="booking-value-compact fw-bold">{maskPhone(keyInfo.guestDetails.phone)}</span>
              </div>
            </div>
          )}

        </div>

        {/* Date-based Action Groups */}
        <div className="booking-date-groups">
          {dateGroups.map((dateGroup, groupIdx) => {
            const groupKey = `date-group-${groupIdx}`;
            const isGroupOpen = openAccordions[groupKey] !== undefined ? openAccordions[groupKey] : false;
            // Hide booking_intent_triggered actions
            const visibleActions = dateGroup.actions.filter(
              (r) => r.bookingData?.action !== "booking_intent_triggered"
            );

            return (
              <div key={groupKey} className="booking-date-group-card mb-3">
                {/* Date Header */}
                <div
                  className="booking-date-group-header"
                  onClick={() => toggleAccordion(groupKey)}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-calendar3 text-primary"></i>
                      <div>
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-semibold small">
                            {formatDisplayDate(dateGroup.checkinDate)} - {formatDisplayDate(dateGroup.checkoutDate)}
                          </span>
                          {dateGroup.actions.some((r) => r.bookingData?.action === "booking_confirmed") && (
                            <span className="badge bg-success-subtle text-success small">
                              <i className="bi bi-check-circle-fill me-1"></i>Booking Confirmed
                            </span>
                          )}
                        </div>
                        <div className="text-muted small">
                          {calculateNights(dateGroup.checkinDate, dateGroup.checkoutDate)}{" "}
                          {calculateNights(dateGroup.checkinDate, dateGroup.checkoutDate) === 1 ? "night" : "nights"} • {visibleActions.length} {visibleActions.length === 1 ? "action" : "actions"}
                        </div>
                      </div>
                    </div>
                    <i className={`bi bi-chevron-${isGroupOpen ? "up" : "down"} text-muted`}></i>
                  </div>
                </div>

                {/* Actions List */}
                {isGroupOpen && (
                  <div className="booking-date-group-actions">
                    {visibleActions.map((record, actionIdx) => {
                      const { action, data } = record.bookingData;
                      const actionKey = `${groupKey}-action-${actionIdx}`;
                      const isActionOpen = openAccordions[actionKey];

                      const getActionInfo = () => {
                        switch (action) {
                          case "occupancy_selected":
                            return { label: "Occupancy Selected", icon: "people", color: "info" };
                          case "rooms_presented":
                            return { label: "Rooms Presented", icon: "door-open", color: "primary" };
                          case "room_selected":
                            return { label: "Room Selected", icon: "check-circle", color: "success" };
                          case "offer_selected":
                            return { label: "Offer Selected", icon: "tag", color: "warning" };
                          case "booking_confirmed":
                            return { label: "Booking Confirmed", icon: "check-circle-fill", color: "success" };
                          default:
                            return { label: action.replace(/_/g, " "), icon: "circle", color: "secondary" };
                        }
                      };

                      const actionInfo = getActionInfo();

                      const isAccordionAction =
                        action === "occupancy_selected" ||
                        action === "rooms_presented" ||
                        action === "offer_selected" ||
                        action === "booking_confirmed";

                      // For booking_confirmed, find the most recent related occupancy_selected in this date group
                      let bookingFinalRateDisplay = null;
                      if (action === "booking_confirmed") {
                        const relatedOccupancy = [...dateGroup.actions.slice(0, actionIdx)]
                          .reverse()
                          .find((r) => r.bookingData?.action === "occupancy_selected");
                        const finalRate =
                          relatedOccupancy?.bookingData?.data?.final_rate ??
                          relatedOccupancy?.bookingData?.data?.finalRate;

                        if (finalRate !== undefined && finalRate !== null) {
                          if (typeof finalRate === "string") {
                            bookingFinalRateDisplay = finalRate;
                          } else {
                            const num = Number(finalRate);
                            if (!Number.isNaN(num)) {
                              bookingFinalRateDisplay = num.toLocaleString();
                            }
                          }
                        }
                      }

                      return (
                        <div key={record.timestamp} className="booking-action-item-compact mb-2">
                          <div
                            className="booking-action-header-compact"
                            onClick={isAccordionAction ? () => toggleAccordion(actionKey) : undefined}
                            style={{ cursor: isAccordionAction ? "pointer" : "default" }}
                          >
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center gap-2">
                                <i className={`bi bi-${actionInfo.icon} text-${actionInfo.color}`}></i>
                                <span className="small fw-medium">{actionInfo.label}</span>
                                {action === "rooms_presented" && (
                                  data.rooms_available === false ? (
                                    <span className="badge bg-danger-subtle text-danger small">No Availability</span>
                                  ) : (
                                    <span className="badge bg-success-subtle text-success small">
                                      {data.total_rooms_count || data.rooms?.length || 0} Available
                                    </span>
                                  )
                                )}
                                {action === "room_selected" && data.room_name && (
                                  <span className="text-muted small">
                                    • {data.room_name}
                                    {data.package_name && ` • ${data.package_name}`}
                                  </span>
                                )}
                                {action === "offer_selected" && data.room_name && (
                                  <span className="text-muted small">
                                    • {data.room_name}
                                    {data.package_name && ` • ${data.package_name}`}
                                  </span>
                                )}
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <span className="text-muted small">
                                  {formatDisplayDate(record.msg_time)} {formatDisplayTime(record.msg_time)}
                                </span>
                                {(action === "occupancy_selected" || action === "rooms_presented" || action === "offer_selected" || action === "booking_confirmed") && (
                                  <i className={`bi bi-chevron-${isActionOpen ? "up" : "down"} small text-muted`}></i>
                                )}
                              </div>
                            </div>
                          </div>

                          {isActionOpen && (
                            <div className="booking-action-details-compact">
                              {action === "occupancy_selected" && (
                                <>
                                  {/* Totals row */}
                                  <div className="d-flex flex-wrap gap-3 mb-2">
                                    <div className="detail-item-compact mb-0">
                                      <span className="text-muted small">Rooms: </span>
                                      <span className="fw-semibold small">{data.total_rooms || 1}</span>
                                    </div>
                                    <div className="detail-item-compact mb-0">
                                      <span className="text-muted small">Adults: </span>
                                      <span className="fw-semibold small">{data.total_adults || 0}</span>
                                    </div>
                                    {data.total_children !== undefined && (
                                      <div className="detail-item-compact mb-0">
                                        <span className="text-muted small">Children: </span>
                                        <span className="fw-semibold small">{data.total_children || 0}</span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Room wise occupancy */}
                                  {Array.isArray(data.room_wise_occupancy) && data.room_wise_occupancy.length > 0 && (
                                    <div className="mt-1">
                                      <div className="fw-semibold small mb-1 text-muted">
                                        Room-wise occupancy
                                      </div>
                                      <div className="d-flex flex-column gap-1">
                                        {data.room_wise_occupancy.map((roomOcc, idx) => (
                                          <div
                                            key={idx}
                                            className="border rounded-2 px-2 py-1 bg-light-subtle d-flex justify-content-between align-items-center"
                                          >
                                            <span className="small fw-semibold">
                                              Room {roomOcc.room}
                                            </span>
                                            <span className="small text-muted">
                                              {roomOcc.adult_count || 0} adults
                                              {roomOcc.child_count !== undefined && (
                                                <> • {roomOcc.child_count || 0} children</>
                                              )}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}

                              {action === "rooms_presented" && data.rooms && data.rooms.length > 0 && (
                                <div className="rooms-list-compact">
                                  {data.rooms.map((room, idx) => (
                                    <div key={idx} className="room-item-compact">
                                      <div className="d-flex justify-content-between mb-1">
                                        <span className="fw-medium small">
                                          {room.room_name}
                                          {room.package_name && (
                                            <span className="text-muted"> • {room.package_name}</span>
                                          )}
                                        </span>
                                        {room.rate !== undefined && (
                                          <span className="text-primary small fw-semibold">
                                            {data.currency ? `${data.currency} ` : ""}
                                            {Number(room.rate).toLocaleString()}/night
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-muted small">
                                        {room.package_name || "N/A"} • Up to {room.max_guest || room.max_adult} guests
                                      </div>
                                      {(room.max_adult !== undefined || room.max_child !== undefined) && (
                                        <div className="text-muted small">
                                          {room.max_adult !== undefined && <>Adults: {room.max_adult}</>}
                                          {room.max_child !== undefined && (
                                            <>
                                              {room.max_adult !== undefined ? " • " : ""}
                                              Children: {room.max_child || 0}
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {action === "offer_selected" && (
                                <>
                                  <div className="d-flex flex-wrap gap-3 mb-2">
                                    {data.room_name && (
                                      <div className="detail-item-compact mb-0">
                                        <span className="text-muted small">Room: </span>
                                        <span className="fw-semibold small">{data.room_name}</span>
                                      </div>
                                    )}
                                    {data.package_name && (
                                      <div className="detail-item-compact mb-0">
                                        <span className="text-muted small">Package: </span>
                                        <span className="fw-semibold small">{data.package_name}</span>
                                      </div>
                                    )}
                                    {data.rate !== undefined && (
                                      <div className="detail-item-compact mb-0">
                                        <span className="text-muted small">Rate: </span>
                                        <span className="fw-semibold small">
                                          {data.currency ? `${data.currency} ` : ""}
                                          {Number(data.rate).toLocaleString()}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}

                              {/* room_selected now only shows room & package in the header; no extra details */}

                              {action === "booking_confirmed" && (
                                <>
                                  {/* Booking ID and final rate only (other details kept for future use) */}
                                  <div className="d-flex flex-wrap gap-3 mb-1 align-items-center">
                                    {data.booking_id && (
                                      <div className="detail-item-compact mb-0">
                                        <span className="text-muted small">Booking ID: </span>
                                        <span className="fw-semibold small text-success">
                                          {data.booking_id}
                                        </span>
                                      </div>
                                    )}
                                    {bookingFinalRateDisplay && (
                                      <div className="detail-item-compact mb-0">
                                        <span className="text-muted small">Final rate: </span>
                                        <span className="fw-semibold small">
                                          {bookingFinalRateDisplay}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/*
                                  // Booking summary, payment, occupancy, selected room,
                                  // room-wise occupancy, and guest details are temporarily
                                  // disabled as per request. They can be re-enabled later.
                                  */}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default BookingDetailsModal;
